import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, StyleSheet, Text, View } from 'react-native';

import { lowerDisplayLineTestIDs, Theme } from '@constants';
import { useShowNextLDLProduct, useTwoDayForecast } from '@hooks';

import { getForecastWeatherPhrase } from '../../../helpers/getWeatherPhrase';
import { isAndroid, isX1 } from '../../../helpers/Platform';
import { TWCAnimatedIcon } from '../../primitives/TWCAnimatedIcon/TWCAnimatedIcon';
import { TWCText } from '../../primitives/TWCText/TWCText';
import { determineProductLabel } from '../determineProductLabel';

const testIDPath = lowerDisplayLineTestIDs.twoDay;
const tPath = 'lowerDisplayLine.twoDay.';

const LDLTwoDayAPI = () => {
  const data = useTwoDayForecast();
  const showNextProduct = useShowNextLDLProduct();

  useEffect(() => {
    !data && showNextProduct(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return data ? <LDLTwoDayUI data={data} onFinish={showNextProduct} /> : <></>;
};

const LDLTwoDayUI = ({ data, onFinish }) => {
  const { t } = useTranslation();
  const { page1, page2 } = data;
  const daypartName = page1[0].daypartName;
  const dayOfWeekToday = page1[0].dayOfWeekToday;

  const pageOneAnimation = useRef(
    new Animated.Value(isAndroid ? Theme.px(9) : isX1 ? Theme.px(-20) : Theme.px(7)),
  ).current;
  const finalShowValue = useRef(new Animated.Value(0)).current;
  const pageSlideAnimation = Animated.timing(pageOneAnimation, {
    delay: 5000,
    toValue: isAndroid ? Theme.px(-91) : isX1 ? Theme.px(-118) : Theme.px(-91), // TODO: fix the android value
    duration: 500,
    useNativeDriver: true,
  });
  const finalShow = Animated.timing(finalShowValue, {
    duration: 5000,
    useNativeDriver: true,
  });

  // TODO: may have to change implementation when integrating TwoDay and ThreeDay
  const animationSequence = data ? [pageSlideAnimation] : [];

  useEffect(() => {
    const animation = Animated.sequence([...animationSequence, finalShow]);

    animation.start(({ finished }) => finished && onFinish?.());
    return animation.reset;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <View
        style={[
          styles.flexDirectionRow,
          {
            transform: [
              {
                translateY: isAndroid ? Theme.px(1) : isX1 ? Theme.px(2) : Theme.px(0),
              },
            ],
          },
        ]}
      >
        <TWCText style={styles.productText} testID={testIDPath.productLabel} variant={'condensedText36Transparent'}>
          {dayOfWeekToday && daypartName ? determineProductLabel(dayOfWeekToday, daypartName)[0] : ''}
        </TWCText>
      </View>
      <View style={styles.forecasts}>
        <Animated.View
          style={[
            styles.animatedView,
            {
              transform: [{ translateY: pageOneAnimation }],
            },
          ]}
          useNativeDriver
        >
          <View style={styles.flexDirectionRow}>
            {page1.map(dayOne => {
              const {
                key,
                iconCodeExtend,
                temperatureHigh,
                temperatureLow,
                dayAbbreviation,
                precipChance,
                precipType,
              } = dayOne;

              const weatherPhrase = getForecastWeatherPhrase({
                iconCodeExtend,
              });

              const precipText = precipChance ? t(`${tPath}precipText`, { precipChance, precipType }) : '';

              return (
                <View key={`${key}`} style={styles.dayItem}>
                  {determineProductLabel(dayOfWeekToday) !== daypartName && (
                    <TWCText
                      style={styles.dayOfWeek}
                      testID={testIDPath.dayOfWeek}
                      variant={'condensedText36Transparent'}
                    >
                      {dayAbbreviation ? dayAbbreviation : ''}
                    </TWCText>
                  )}
                  <TWCAnimatedIcon code={iconCodeExtend} />
                  <View style={styles.itemText}>
                    <Text>
                      <TWCText testID={testIDPath.temperatureHigh} variant={'condensedText36'}>
                        {temperatureHigh}
                        {temperatureHigh && temperatureLow ? '/' : ''}
                      </TWCText>
                      <TWCText
                        style={styles.temperatureLow}
                        testID={testIDPath.temperatureLow}
                        variant={'condensedText36'}
                      >
                        {temperatureLow}
                      </TWCText>
                    </Text>
                  </View>
                  <TWCText style={styles.itemText} testID={testIDPath.weatherPhrase} variant={'condensedText36'}>
                    {weatherPhrase ?? ''}
                  </TWCText>
                  <TWCText style={styles.itemText} testID={testIDPath.precipChance} variant={'condensedText36'}>
                    {precipText}
                  </TWCText>
                </View>
              );
            })}
          </View>
          <View
            style={[
              styles.flexDirectionRow,
              {
                transform: [{ translateY: Theme.px(50) }],
              },
            ]}
          >
            {page2.map(dayTwo => {
              const {
                key,
                iconCodeExtend,
                temperatureHigh,
                temperatureLow,
                dayAbbreviation,
                precipChance,
                precipType,
              } = dayTwo;

              const weatherPhrase = getForecastWeatherPhrase({
                iconCodeExtend,
              });

              const precipText = precipChance ? `${precipChance} Chance of ${precipType}` : '';

              return (
                <View key={`${key}`} style={styles.dayItem}>
                  {determineProductLabel(dayOfWeekToday) !== daypartName && (
                    <TWCText
                      style={styles.dayOfWeek}
                      testID={testIDPath.dayOfWeek}
                      variant={'condensedText36Transparent'}
                    >
                      {dayAbbreviation ? dayAbbreviation : ''}
                    </TWCText>
                  )}
                  <TWCAnimatedIcon code={iconCodeExtend} />
                  <View style={styles.itemText}>
                    <Text>
                      <TWCText testID={testIDPath.temperatureHigh} variant={'condensedText36'}>
                        {temperatureHigh}
                        {temperatureHigh && temperatureLow ? '/' : ''}
                      </TWCText>
                      <TWCText
                        style={styles.temperatureLow}
                        testID={testIDPath.temperatureLow}
                        variant={'condensedText36'}
                      >
                        {temperatureLow}
                      </TWCText>
                    </Text>
                  </View>
                  <View>
                    <TWCText style={styles.itemText} testID={testIDPath.weatherPhrase} variant={'condensedText36'}>
                      {weatherPhrase ? weatherPhrase : ''}
                    </TWCText>
                  </View>
                  <View>
                    <TWCText style={styles.itemText} testID={testIDPath.precipChance} variant={'condensedText36'}>
                      {precipText}
                    </TWCText>
                  </View>
                </View>
              );
            })}
          </View>
        </Animated.View>
      </View>
    </>
  );
};

export const LDLTwoDay = {
  API: LDLTwoDayAPI,
  UI: LDLTwoDayUI,
};

const styles = StyleSheet.create({
  animatedView: {
    position: 'absolute',
  },
  dayItem: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  dayOfWeek: {
    paddingRight: Theme.px(18),
  },
  forecasts: {
    flexGrow: 1,
    height: '100%',
  },
  itemText: {
    paddingLeft: Theme.px(18),
    paddingRight: Theme.px(42),
  },
  pageWrapper: {
    flexDirection: 'row',
    position: 'absolute',
  },
  productText: {
    paddingRight: Theme.px(48),
  },
  temperatureLow: {
    color: Theme.malibuBlue,
  },
  flexDirectionRow: {
    flexDirection: 'row',
  },
});
